import { Component, OnInit, ElementRef, Renderer2, ViewChild, OnDestroy, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import { LayoutService } from '../services/layout.service';
import { Subscription } from 'rxjs';
import { UntypedFormGroup, UntypedFormBuilder, ValidatorFn, AbstractControl, ValidationErrors, ReactiveFormsModule } from '@angular/forms';
import { Filter } from 'app/model';
import { formatDDMMYYYY, formatISODate, isAfterDay, isBeforeDay } from '../utils/date-format';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { NgSelectModule } from '@ng-select/ng-select';
import { FlowFilterComponent } from '../flow-filter/flow-filter.component';
import { SectorFilterComponent } from '../sector-filter/sector-filter.component';
import { NgIf } from '@angular/common';

@Component({
    selector: 'app-notification-sidebar',
    templateUrl: './notification-sidebar.component.html',
    styleUrls: ['./notification-sidebar.component.scss'],
    imports: [ReactiveFormsModule, NgIf, SectorFilterComponent, FlowFilterComponent, NgSelectModule, MatSlideToggleModule]
})
export class NotificationSidebarComponent implements OnInit, OnDestroy, OnChanges {

  filtersForm: UntypedFormGroup;
  layoutSub: Subscription;
  isOpen = false;
  datePickerOptions = {
    locale: {
      format: 'DD/MM/YYYY',
      separator: ' a ',
      applyLabel: 'Aplicar',
      cancelLabel: 'Cancelar',
      fromLabel: 'Desde',
      toLabel: 'Hasta',
      customRangeLabel: 'Custom',
      weekLabel: 'S',
      daysOfWeek: [
        'Do',
        'Lu',
        'Ma',
        'Mi',
        'Ju',
        'Vi',
        'Sa'
      ],
      monthNames: [
        'Enero',
        'Febrero',
        'Marzo',
        'Abril',
        'Mayo',
        'Junio',
        'Julio',
        'Agosto',
        'Septiembre',
        'Octubre',
        'Noviembre',
        'Diciembre'
      ],
      firstDay: 1
    },
    autoApply: true,
    opens: 'left',
    applyButtonClasses: 'btn-primary white'
  };

  @Input() flows = [];
  @Input() sectors = [];
  @Input() machines = [];
  @Input() taskStatusesFilter = [];
  @Input() dateFrom: string;
  @Input() dateTo: string;
  @Input() showFlowColumn: boolean;
  @Input() showSectorColumn: boolean;
  @Input() ganttViewMode: string;

  @ViewChild('sidebar', { static: false }) sidebar: ElementRef;

  @Output() sendFilters = new EventEmitter();
  @Output() columnVisibilityChange = new EventEmitter<string>();
  @Output() ganttVisibilityChange = new EventEmitter<string>();

  sectorControls: any;
  flowControls: any;
  selectedView: string;
  ganttMinDate: string;
  ganttMaxDate: string;

  constructor(
    private elRef: ElementRef,
    private renderer: Renderer2,
    private fb: UntypedFormBuilder,
    private layoutService: LayoutService) {
    this.layoutSub = layoutService.changeEmitted$.subscribe(
      value => {
        if (this.isOpen) {
          this.renderer.removeClass(this.sidebar.nativeElement, 'open');
          this.isOpen = false;
        } else {
          this.renderer.addClass(this.sidebar.nativeElement, 'open');
          this.isOpen = true;
        }
      });
  }

  ngOnInit() {
    this.initializeForm();
  }

  ngOnChanges() {
    if (this.filtersForm) {
      this.filtersForm.get('showSector').setValue(this.showSectorColumn);
      this.filtersForm.get('showFlow').setValue(this.showFlowColumn);
    }
  }

  ngOnDestroy() {
    if (this.layoutSub) {
      this.layoutSub.unsubscribe();
    }
  }

  private initializeForm(): void {
    this.filtersForm = this.fb.group({
      idSector: this.fb.group({
        idSector: ['']
      }),
      idFlow: this.fb.group({
        idFlow: ['']
      }),
      dateFrom: ['', ],
      dateTo: ['', ],
      idMachine: ['', ],
      showSector: [false, ],
      showFlow: [false, ],
      status: ['', ],
      view: ['BOTH', ]
    }, { validators: this.dateRangeValidator });

    this.sectorControls = this.filtersForm.controls.idSector;
    this.flowControls = this.filtersForm.controls.idFlow;

    // Limita el rango de fechas seleccionable a los bordes del gantt.
    if (this.dateFrom) {
      const formattedDateFrom = formatDDMMYYYY(this.dateFrom);
      // this.datePickerOptions['minDate'] = formattedDateFrom;
      this.datePickerOptions['startDate'] = formattedDateFrom;
      this.ganttMinDate = formatISODate(this.dateFrom);
    }
    if (this.dateTo) {
      const formattedDateTo = formatDDMMYYYY(this.dateTo);
      // this.datePickerOptions['maxDate'] = formattedDateTo;
      this.datePickerOptions['endDate'] = formattedDateTo;
      this.ganttMaxDate = formatISODate(this.dateTo);
    }
  }

  // Valida el rango de fechas de los filtros. Ambas fechas son opcionales, pero:
  //  - "Hasta" no puede ser anterior a "Desde"
  //  - ninguna puede caer fuera de los bordes del gantt (ganttMinDate/ganttMaxDate)
  private dateRangeValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
    const from = group.get('dateFrom').value;
    const to = group.get('dateTo').value;

    if (from && to && isBeforeDay(to, from)) {
      return { dateRange: true };
    }

    const min = this.ganttMinDate;
    const max = this.ganttMaxDate;
    const outOfBounds = (value: string) =>
      !!value &&
      ((min && isBeforeDay(value, min)) ||
        (max && isAfterDay(value, max)));

    if (outOfBounds(from) || outOfBounds(to)) {
      return { dateOutOfBounds: true };
    }

    return null;
  };

  onClose() {
    this.renderer.removeClass(this.sidebar.nativeElement, 'open');
    this.isOpen = false;
  }

  updateFormValue(eventData: Event, formControlName: string) {
    this.filtersForm.controls[formControlName].get(formControlName).setValue(eventData);
  }

  applyFilters() {
    const filter = new Filter(this.filtersForm.value);
    const empty = Object.getOwnPropertyNames(filter).length === 0;
    let filtersData;

    if (empty) {
      filtersData = null;
    } else {
      filtersData = filter;
    }
    this.sendFilters.emit(filtersData);
    this.layoutService.emitChange(false);
  }

  changeColumnVisibility(columnName: string) {
    this.columnVisibilityChange.emit(columnName);
  }

  changeGanttVisibility() {
    this.ganttViewMode = this.filtersForm.get('view').value;
    this.ganttVisibilityChange.emit(this.ganttViewMode);
  }

  selectedDate(value: any) {
    const start = value.start;
    const end = value.end;
    this.filtersForm.get('dateFrom').setValue(formatISODate(start, '/'));
    this.filtersForm.get('dateTo').setValue(formatISODate(end, '/'));
  }

  resetFilters() {
    this.filtersForm['controls']['idSector']['controls']['idSector'].reset();
    this.filtersForm['controls']['idFlow']['controls']['idFlow'].reset();
    this.filtersForm.get('status').setValue('');
    this.filtersForm.get('dateFrom').setValue('');
    this.filtersForm.get('dateTo').setValue('');
    this.filtersForm.get('idMachine').setValue('Todas');
    this.filtersForm.get('view').setValue('BOTH');
    this.selectedView = 'BOTH';
    this.showFlowColumn = false;
    this.showSectorColumn = false;
    this.filtersForm.updateValueAndValidity();
    this.sendFilters.emit(undefined);
    this.layoutService.emitChange(false);
  }

}
